<template>
  <SlottedContainerComponent
    :buttonText="expandButtonText ?? ''"
    :expandable="expandable && hasExpandableContent"
    :collapsed="!expanded"
    :containerClass="expandable ? 'card shadow-sm' : 'card shadow-sm h-100'"
    role="article"
    :aria-label="ariaLabel"
    button-alignment="left"
    :useCardPadding="true"
    :framedContent
    @setForceToggle="emit('setForceToggle', $event)"
    @toggled="emit('toggled', $event)"
  >
    <template #title>
      <div class="w-100">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <div
              class="icon-box rounded-3 me-3 d-flex align-items-center justify-content-center text-white"
              :style="{ backgroundColor: color }"
            >
              <FontAwesomeIcon :icon="icon" />
            </div>
            <div>
              <h5 class="mb-0 fw-semibold">{{ title }}</h5>
              <div class="mt-2">
                <slot name="badge" />
              </div>
            </div>
          </div>
          <div class="text-end">
            <div class="d-flex align-items-center">
              <div class="me-3">
                <div class="d-flex align-items-baseline gap-1">
                  <h3 class="mb-0 fw-normal">~{{ formattedAmount }}</h3>
                  <h4 class="mb-0 fw-normal fs-9 text-muted">{{ unit }}</h4>
                </div>
                <small class="text-muted">{{ subtitle }}</small>
              </div>
              <slot name="action" />
            </div>
          </div>
        </div>
        <slot name="header-extra" />
      </div>
    </template>
    <template v-if="expandable" #content>
      <slot name="content" />
    </template>
  </SlottedContainerComponent>
</template>

<script setup lang="ts">
import SlottedContainerComponent from "@/components/common/SlottedContainerComponent.vue";
import { formatMwh } from "@/composables/formatUtils";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    icon: string | string[];
    color: string;
    title: string;
    amount: number;
    unit: string;
    subtitle: string;
    ariaLabel: string;
    expandable?: boolean;
    expandButtonText?: string;
    expanded?: boolean;
    hasExpandableContent?: boolean;
    /** See `SlottedContainerComponent.framedContent`. */
    framedContent?: boolean;
  }>(),
  {
    expandable: false,
    expanded: false,
    hasExpandableContent: true,
    framedContent: true,
  }
);

const emit = defineEmits<{
  setForceToggle: [fn: (value: boolean) => void];
  toggled: [expanded: boolean];
}>();

const formattedAmount = computed(() => formatMwh(props.amount));
</script>

<style scoped lang="scss">
.icon-box {
  width: 48px;
  height: 48px;
  font-size: 1.25rem;
}
</style>
